/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FIND_FEATURE_ASSIGNABLE_WORKSPACES,
  SortDirectionEnum,
} from '@gripp/shared-logic';
import { AssignmentListGrid, BaseColumnCheckbox, Colors } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleProp, StyleSheet, Text } from 'react-native';

export type FeatureWorkspaceAssignmentListProps = {
  featureId: string;
  maxHeight?: number;
  keyword?: string;
  workspaceAssignments: string[];
  onWorkspaceAssignmentsChange: (assignments: string[]) => void;
};

export const FeatureWorkspaceAssignmentList = ({
  featureId,
  maxHeight,
  keyword,
  workspaceAssignments,
  onWorkspaceAssignmentsChange,
}: FeatureWorkspaceAssignmentListProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<any>(() => {
    if (featureId) {
      return {
        keyword,
        featureId,
        assignments: workspaceAssignments,
      };
    }
    return undefined;
  });

  useEffect(() => {
    setFilter({
      keyword,
      featureId,
      assignments: workspaceAssignments,
    });
  }, [keyword, featureId]);

  const columnHelper = createColumnHelper<any>();

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const columns = [
    columnHelper.accessor('selected', {
      cell: BaseColumnCheckbox.checkbox,
      header: BaseColumnCheckbox.checkboxHeader,
      meta: { style: styles.checkboxCell },
    }),
    columnHelper.accessor('name', {
      cell: (info) => getLabel(styles.nameLabel, info.getValue()),
      header: () => t('features.assignment.name'),
      meta: { style: styles.cell },
    }),
  ] as ColumnDef<any, any>[];

  if (!filter) return null;

  return (
    <AssignmentListGrid
      modelName="workspace"
      query={FIND_FEATURE_ASSIGNABLE_WORKSPACES}
      dataPrefix="feature.findAssignableWorkspaces"
      columns={columns}
      filter={filter}
      assignments={workspaceAssignments}
      onAssignmentsChange={onWorkspaceAssignmentsChange}
      maxHeight={maxHeight}
      textEmpty={t('features.assignment.noWorkspaces')}
      orderBy={{ field: 'name', order: SortDirectionEnum.Asc }}
    />
  );
};

const styles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: {
    color: Colors.role.textStrong,
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
    color: Colors.role.textStrong,
  },
});
